import { Injectable, computed, effect, signal } from '@angular/core';
import { Observable, asyncScheduler, firstValueFrom, scheduled, throwError } from 'rxjs';
import { catchError, finalize, shareReplay, tap } from 'rxjs/operators';
import { createStudioId } from '../models/component-studio.models';
import {
  CanvasThemeMode,
  CanvasThemePackage,
  CanvasThemeTokenSet,
  DEFAULT_CANVAS_THEME_DARK,
  DEFAULT_CANVAS_THEME_LIGHT,
  DEFAULT_CANVAS_THEME_PACKAGE
} from '../models/component-canvas-theme.models';
import {
  ComponentCanvasThemeApiError,
  ComponentCanvasThemeApiService
} from './component-canvas-theme-api.service';

@Injectable({
  providedIn: 'root'
})
export class ComponentCanvasThemeService {
  private readonly legacyPackagesStorageKey = 'bw.component-studio.canvas-theme-packages';
  private readonly pendingPackagesStorageKey = 'bw.component-studio.canvas-theme-pending';
  private readonly selectedPackageStorageKey = 'bw.component-studio.canvas-theme-selected';
  private readonly previewModeStorageKey = 'bw.component-studio.canvas-theme-mode';
  private readonly saveIdleDelayMs = 10000;
  private readonly saveMaxDelayMs = 60000;

  private readonly packagesSignal = signal<CanvasThemePackage[]>([]);
  private readonly selectedPackageIdSignal = signal<string>(this.loadSelectedPackageId());
  private readonly previewModeSignal = signal<CanvasThemeMode>(this.loadPreviewMode());
  private readonly loadedSignal = signal(false);
  private readonly loadingSignal = signal(false);
  private readonly loadErrorSignal = signal<string | null>(null);
  private readonly savingSignal = signal(false);
  private readonly saveErrorSignal = signal<string | null>(null);
  private readonly hasPendingChangesSignal = signal(false);

  private loadRequest$: Observable<CanvasThemePackage[]> | null = null;
  private saveTimer: ReturnType<typeof setTimeout> | null = null;
  private maxSaveTimer: ReturnType<typeof setTimeout> | null = null;
  private dirtySince: number | null = null;
  private lastSavedSerialized = '[]';
  private savePromise: Promise<void> | null = null;

  readonly packages = computed(() => [DEFAULT_CANVAS_THEME_PACKAGE, ...this.packagesSignal()]);
  readonly selectedPackageId = computed(() => {
    const currentId = this.selectedPackageIdSignal();
    return this.packages().some((themePackage) => themePackage.id === currentId) ? currentId : 'default';
  });
  readonly previewMode = computed(() => this.previewModeSignal());
  readonly selectedPackage = computed<CanvasThemePackage>(() => {
    return this.packages().find((themePackage) => themePackage.id === this.selectedPackageId()) ?? DEFAULT_CANVAS_THEME_PACKAGE;
  });
  readonly effectiveTokens = computed<CanvasThemeTokenSet>(() => this.resolveTokens(this.selectedPackageId(), this.previewMode()));
  readonly isLoaded = computed(() => this.loadedSignal());
  readonly isLoading = computed(() => this.loadingSignal());
  readonly loadError = computed(() => this.loadErrorSignal());
  readonly isSaving = computed(() => this.savingSignal());
  readonly saveError = computed(() => this.saveErrorSignal());
  readonly hasPendingChanges = computed(() => this.hasPendingChangesSignal());

  constructor(private readonly componentCanvasThemeApiService: ComponentCanvasThemeApiService) {
    effect(() => {
      if (typeof window === 'undefined') {
        return;
      }

      window.localStorage.setItem(this.selectedPackageStorageKey, this.selectedPackageIdSignal());
    });

    effect(() => {
      if (typeof window === 'undefined') {
        return;
      }

      window.localStorage.setItem(this.previewModeStorageKey, this.previewMode());
    });

  }

  loadPackages(force = false): Observable<CanvasThemePackage[]> {
    if (this.loadedSignal() && !force) {
      return scheduled([this.packagesSignal()], asyncScheduler);
    }

    if (this.loadRequest$ && !force) {
      return this.loadRequest$;
    }

    this.loadingSignal.set(true);
    this.loadErrorSignal.set(null);

    const request$ = this.componentCanvasThemeApiService.getPackages().pipe(
      tap((packages) => {
        const mergedPackages = this.mergeLegacyPackages(packages);
        this.packagesSignal.set(mergedPackages);
        this.loadedSignal.set(true);
        this.lastSavedSerialized = this.serializePackages(mergedPackages);
        const restoredPending = this.restorePendingPackages();
        if (!restoredPending && mergedPackages.length !== packages.length) {
          this.markStateChanged();
        } else if (!restoredPending) {
          this.hasPendingChangesSignal.set(false);
          this.clearPendingPackagesStorage();
          this.clearScheduledSave();
          this.dirtySince = null;
        }
      }),
      catchError((error) => {
        this.loadErrorSignal.set(this.resolveErrorMessage(error));
        return throwError(() => error);
      }),
      finalize(() => {
        this.loadingSignal.set(false);
        this.loadRequest$ = null;
      }),
      shareReplay(1)
    );

    this.loadRequest$ = request$;
    return request$;
  }

  setSelectedPackage(packageId: string): void {
    this.selectedPackageIdSignal.set(packageId);
  }

  setPreviewMode(mode: CanvasThemeMode): void {
    this.previewModeSignal.set(mode);
  }

  createPackage(): CanvasThemePackage {
    const themePackage: CanvasThemePackage = {
      id: createStudioId('canvas-theme'),
      name: 'New theme package',
      description: '',
      lightOverrides: {},
      darkOverrides: {}
    };

    this.packagesSignal.update((packages) => [themePackage, ...packages]);
    this.selectedPackageIdSignal.set(themePackage.id);
    this.markStateChanged();
    return structuredClone(themePackage);
  }

  getPackageForEdit(packageId: string): CanvasThemePackage | null {
    const themePackage = this.packages().find((item) => item.id === packageId);
    return themePackage ? structuredClone(themePackage) : null;
  }

  savePackage(themePackage: CanvasThemePackage): void {
    if (themePackage.id === 'default') {
      return;
    }

    const sanitizedPackage: CanvasThemePackage = {
      ...themePackage,
      name: themePackage.name.trim() || 'Untitled theme package',
      description: themePackage.description.trim(),
      lightOverrides: sanitizeOverrides(themePackage.lightOverrides),
      darkOverrides: sanitizeOverrides(themePackage.darkOverrides)
    };

    this.packagesSignal.update((packages) => {
      const existingIndex = packages.findIndex((item) => item.id === sanitizedPackage.id);
      if (existingIndex === -1) {
        return [sanitizedPackage, ...packages];
      }

      const nextPackages = [...packages];
      nextPackages[existingIndex] = sanitizedPackage;
      return nextPackages;
    });

    this.markStateChanged();
  }

  deletePackage(packageId: string): void {
    if (packageId === 'default') {
      return;
    }

    this.packagesSignal.update((packages) => packages.filter((themePackage) => themePackage.id !== packageId));
    if (this.selectedPackageId() === packageId) {
      this.selectedPackageIdSignal.set('default');
    }

    this.markStateChanged();
  }

  buildCanvasVariableStyle(packageId = this.selectedPackageId(), mode = this.previewMode()): Record<string, string> {
    const tokens = this.resolveTokens(packageId, mode);
    return {
      '--bw-canvas-primary': tokens.primary,
      '--bw-canvas-secondary': tokens.secondary,
      '--bw-canvas-background-color': tokens.background,
      '--bw-canvas-stage-background': tokens.stageBackground,
      '--bw-canvas-surface-color': tokens.surface,
      '--bw-canvas-surface-muted-color': tokens.surfaceMuted,
      '--bw-canvas-border-color': tokens.border,
      '--bw-canvas-text-color': tokens.text,
      '--bw-canvas-muted-color': tokens.muted,
      '--bw-canvas-shadow': tokens.shadow
    };
  }

  private resolveTokens(packageId: string, mode: CanvasThemeMode): CanvasThemeTokenSet {
    const baseTokens = mode === 'dark' ? DEFAULT_CANVAS_THEME_DARK : DEFAULT_CANVAS_THEME_LIGHT;
    const selectedPackage = this.packages().find((themePackage) => themePackage.id === packageId);
    if (!selectedPackage || selectedPackage.id === 'default') {
      return baseTokens;
    }

    const overrideSet = mode === 'dark' ? selectedPackage.darkOverrides : selectedPackage.lightOverrides;
    return {
      ...baseTokens,
      ...overrideSet
    };
  }

  private loadSelectedPackageId(): string {
    if (typeof window === 'undefined') {
      return 'default';
    }

    return window.localStorage.getItem(this.selectedPackageStorageKey) || 'default';
  }

  private loadPreviewMode(): CanvasThemeMode {
    if (typeof window === 'undefined') {
      return 'light';
    }

    const storedMode = window.localStorage.getItem(this.previewModeStorageKey);
    return storedMode === 'dark' ? 'dark' : 'light';
  }

  private resolveErrorMessage(error: unknown): string {
    const apiError = error as ComponentCanvasThemeApiError | undefined;
    return apiError?.message || 'An unexpected error occurred';
  }

  private mergeLegacyPackages(remotePackages: CanvasThemePackage[]): CanvasThemePackage[] {
    if (typeof window === 'undefined') {
      return remotePackages;
    }

    const legacyPackages = this.loadLegacyPackages();
    if (!legacyPackages.length) {
      return remotePackages;
    }

    const mergedPackages = new Map<string, CanvasThemePackage>();
    remotePackages.forEach((themePackage) => mergedPackages.set(themePackage.id, themePackage));
    legacyPackages.forEach((themePackage) => mergedPackages.set(themePackage.id, themePackage));

    window.localStorage.removeItem(this.legacyPackagesStorageKey);
    return Array.from(mergedPackages.values());
  }

  private loadLegacyPackages(): CanvasThemePackage[] {
    if (typeof window === 'undefined') {
      return [];
    }

    try {
      const storedPackages = window.localStorage.getItem(this.legacyPackagesStorageKey);
      if (!storedPackages) {
        return [];
      }

      const parsedPackages = JSON.parse(storedPackages) as CanvasThemePackage[];
      return Array.isArray(parsedPackages) ? parsedPackages : [];
    } catch {
      return [];
    }
  }

  async flushPendingSave(): Promise<void> {
    if (!this.loadedSignal()) {
      return;
    }

    this.clearScheduledSave();

    while (true) {
      if (this.savePromise) {
        await this.savePromise;
        continue;
      }

      if (!this.hasPersistedStateChanges()) {
        this.hasPendingChangesSignal.set(false);
        this.clearPendingPackagesStorage();
        this.dirtySince = null;
        return;
      }

      await this.performRemoteSave();
    }
  }

  flushPendingSaveOnUnload(): void {
    if (!this.loadedSignal() || !this.hasPersistedStateChanges()) {
      return;
    }

    this.persistPendingPackages();
    this.componentCanvasThemeApiService.savePackagesKeepalive(this.packagesSignal());
  }

  private markStateChanged(): void {
    if (!this.loadedSignal()) {
      return;
    }

    if (!this.hasPersistedStateChanges()) {
      this.hasPendingChangesSignal.set(false);
      this.clearPendingPackagesStorage();
      this.clearScheduledSave();
      this.dirtySince = null;
      return;
    }

    this.hasPendingChangesSignal.set(true);
    this.persistPendingPackages();
    this.scheduleBufferedSave();
  }

  private scheduleBufferedSave(): void {
    if (!this.loadedSignal() || !this.hasPersistedStateChanges()) {
      return;
    }

    if (this.dirtySince === null) {
      this.dirtySince = Date.now();
    }

    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
    }

    this.saveTimer = setTimeout(() => {
      void this.performRemoteSave();
    }, this.saveIdleDelayMs);

    if (!this.maxSaveTimer && this.dirtySince !== null) {
      const elapsed = Date.now() - this.dirtySince;
      const remaining = Math.max(0, this.saveMaxDelayMs - elapsed);
      this.maxSaveTimer = setTimeout(() => {
        void this.performRemoteSave();
      }, remaining);
    }
  }

  private clearScheduledSave(): void {
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
    }

    if (this.maxSaveTimer) {
      clearTimeout(this.maxSaveTimer);
      this.maxSaveTimer = null;
    }
  }

  private async performRemoteSave(): Promise<void> {
    if (!this.loadedSignal() || !this.hasPersistedStateChanges()) {
      return;
    }

    if (this.savePromise) {
      return this.savePromise;
    }

    this.clearScheduledSave();
    this.savingSignal.set(true);
    this.saveErrorSignal.set(null);

    const payload = structuredClone(this.packagesSignal());
    const requestSerialized = this.serializePackages(payload);

    this.savePromise = firstValueFrom(this.componentCanvasThemeApiService.savePackages(payload))
      .then((packages) => {
        const currentSerialized = this.serializePackages();
        const savedSerialized = this.serializePackages(packages);
        this.lastSavedSerialized = savedSerialized;

        if (currentSerialized === requestSerialized) {
          this.packagesSignal.set(packages);
        }

        if (this.hasPersistedStateChanges()) {
          this.hasPendingChangesSignal.set(true);
          this.persistPendingPackages();
          this.dirtySince = Date.now();
          this.scheduleBufferedSave();
          return;
        }

        this.hasPendingChangesSignal.set(false);
        this.clearPendingPackagesStorage();
        this.dirtySince = null;
      })
      .catch((error: unknown) => {
        this.saveErrorSignal.set(this.resolveErrorMessage(error));
        this.hasPendingChangesSignal.set(this.hasPersistedStateChanges());
        this.persistPendingPackages();
        this.dirtySince ??= Date.now();
        this.scheduleBufferedSave();
      })
      .finally(() => {
        this.savingSignal.set(false);
        this.savePromise = null;
      });

    return this.savePromise;
  }

  private hasPersistedStateChanges(): boolean {
    return this.serializePackages() !== this.lastSavedSerialized;
  }

  private serializePackages(packages = this.packagesSignal()): string {
    return JSON.stringify(packages);
  }

  private restorePendingPackages(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    try {
      const rawPackages = window.localStorage.getItem(this.pendingPackagesStorageKey);
      if (!rawPackages) {
        return false;
      }

      const parsedPackages = JSON.parse(rawPackages) as CanvasThemePackage[];
      if (!Array.isArray(parsedPackages)) {
        return false;
      }

      this.packagesSignal.set(parsedPackages);
      if (!this.hasPersistedStateChanges()) {
        this.clearPendingPackagesStorage();
        return false;
      }

      this.hasPendingChangesSignal.set(true);
      this.dirtySince = Date.now();
      this.scheduleBufferedSave();
      return true;
    } catch {
      return false;
    }
  }

  private persistPendingPackages(): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(this.pendingPackagesStorageKey, JSON.stringify(this.packagesSignal()));
  }

  private clearPendingPackagesStorage(): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.removeItem(this.pendingPackagesStorageKey);
  }
}

function sanitizeOverrides(overrides: Partial<CanvasThemeTokenSet>): Partial<CanvasThemeTokenSet> {
  return Object.entries(overrides).reduce<Partial<CanvasThemeTokenSet>>((result, [key, value]) => {
    const normalizedValue = typeof value === 'string' ? value.trim() : value;
    if (!normalizedValue) {
      return result;
    }

    result[key as keyof CanvasThemeTokenSet] = normalizedValue as CanvasThemeTokenSet[keyof CanvasThemeTokenSet];
    return result;
  }, {});
}
